/**
 * CohortAdminView — Cohort detail page for program admins.
 *
 * Tabs: Overview → Activities → Participants → Mentors → Graduation → Metrics
 *
 * Action alerts are computed server-side via GET /cohorts/{id}/action-items
 * and rendered by CohortOverviewTab. No data fetching needed here for alerts.
 */
import { useNavigate } from 'react-router-dom';
import { useHashTab } from '../../hooks/useHashTab';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Tabs from '@cloudscape-design/components/tabs';
import Box from '@cloudscape-design/components/box';
// TODO: Restore Alert and Link imports when isPublished flow is fully tested
// import Alert from '@cloudscape-design/components/alert';
// import Link from '@cloudscape-design/components/link';
import { Cohort } from '../../api/cohorts';
import { ProgramAdmin } from '../../api/programs';
import { useQuery } from '@tanstack/react-query';
import { cohortsApi } from '../../api/cohorts';
import { queryKeys } from '../../lib/queryClient';
import { useMentors, useMentorRequests } from '../../hooks/queries';
import { activitiesApi } from '../../api/activities';
import { CohortOverviewTab } from '../cohort-tabs/CohortOverviewTab';
import { ActivitiesTab } from '../cohort-tabs/ActivitiesTab';
import { ParticipantsTab } from '../cohort-tabs/ParticipantsTab';
import { MentorsTab } from '../cohort-tabs/MentorsTab';
import { GraduationRequestsTab } from '../cohort-tabs/GraduationRequestsTab';
import { ProgressTab } from '../cohort-tabs/MetricsTab';

interface CohortAdminViewProps {
  cohort: Cohort;
  programAdmins: ProgramAdmin[];
  programWikiUrl?: string;
  programName?: string;
  programOwnerId?: string;
  programOwnerName?: string;
}

export function CohortAdminView({ cohort, programAdmins, programWikiUrl, programName, programOwnerId, programOwnerName }: CohortAdminViewProps) {
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useHashTab('overview');

  const { data: mentorsData, refetch: refetchMentors } = useMentors(cohort.cohortId);
  const { data: mentorRequestsData, refetch: refetchMentorRequests } = useMentorRequests(cohort.cohortId);

  const { data: participantsData } = useQuery({
    queryKey: queryKeys.cohorts.participants(cohort.cohortId),
    queryFn: () => cohortsApi.getParticipants(cohort.cohortId),
  });

  const { data: graduationRequestsData } = useQuery({
    queryKey: queryKeys.cohorts.graduationRequests(cohort.cohortId),
    queryFn: () => cohortsApi.listGraduationRequests(cohort.cohortId),
  });

  const { data: activities } = useQuery({
    queryKey: queryKeys.activities.list(cohort.cohortId),
    queryFn: () => activitiesApi.list(cohort.cohortId),
  });

  const activityCount = activities?.length ?? 0;
  const pendingGraduations = graduationRequestsData?.pendingCount ?? 0;
  const participantCount = participantsData?.participantCount;

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <CohortOverviewTab
          cohort={cohort}
          programAdmins={programAdmins}
          programWikiUrl={programWikiUrl}
          programOwnerId={programOwnerId}
          programOwnerName={programOwnerName}
          onNavigateToTab={(tabId) => setActiveTabId(
            tabId === 'graduation-requests' ? 'graduation' : tabId
          )}
        />
      ),
    },
    {
      id: 'activities',
      label: `Activities (${activityCount})`,
      content: (
        <ActivitiesTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
          cohortStartDate={cohort.startDate}
          cohortEndDate={cohort.endDate}
        />
      ),
    },
    {
      id: 'participants',
      label: `Participants${participantCount !== undefined ? ` (${participantCount}${cohort.capacity ? `/${cohort.capacity}` : ''})` : ''}`,
      content: (
        <ParticipantsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
        />
      ),
    },
    ...(cohort.mentorsEnabled ? [{
      id: 'mentors',
      label: `Mentors (${mentorsData?.length || 0})`,
      content: (
        <MentorsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentors={mentorsData || []}
          mentorRequests={mentorRequestsData || []}
          refetchMentors={refetchMentors}
          refetchMentorRequests={refetchMentorRequests}
        />
      ),
    }] : []),
    {
      id: 'graduation',
      label: pendingGraduations > 0 ? `Graduation (${pendingGraduations})` : 'Graduation',
      content: <GraduationRequestsTab cohortId={cohort.cohortId} />,
    },
    {
      id: 'metrics',
      label: 'Metrics',
      content: (
        <ProgressTab
          cohortId={cohort.cohortId}
          mentorsEnabled={cohort.mentorsEnabled}
          showActivityDetails={true}
          onNavigateToTab={setActiveTabId}
        />
      ),
    },
  ];

  return (
    <>
      <SpaceBetween size="m">
        {/* TODO: Uncomment when isPublished flow is fully tested
        {!cohort.isPublished && (
          <Alert type="info">
            This cohort is not yet published. It is only visible to program admins.{' '}
            <Link onFollow={(e) => { e.preventDefault(); navigate(`/cohorts/${cohort.cohortId}/settings`); }} href="#">
              Go to Settings
            </Link>
          </Alert>
        )}
        */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <SpaceBetween size="xxs">
            <Box variant="h1">{programName ?? cohort.cohortName}</Box>
            {programName && <Box variant="h2" color="text-body-secondary">{cohort.cohortName}</Box>}
          </SpaceBetween>
          <Button iconName="settings" onClick={() => navigate(`/cohorts/${cohort.cohortId}/settings`)}>
            Settings
          </Button>
        </div>
        <Tabs activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} tabs={tabs} />
      </SpaceBetween>
    </>
  );
}
